// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import {getMuteDurationOptions} from '@app/features/channel/components/MuteOptions';
import {ChannelSettingsModal} from '@app/features/channel/components/modals/ChannelSettingsModal';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {CheckboxItem} from '@app/features/ui/action_menu/ContextMenu';
import {
	CopyIdIcon,
	DeleteIcon,
	MarkAsReadIcon,
	MuteIcon,
	SettingsIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import itemStyles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import menuItemStyles from '@app/features/ui/action_menu/MenuItem.module.css';
import {MenuItemRadio} from '@app/features/ui/action_menu/MenuItemRadio';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as UserGuildSettingsCommands from '@app/features/user/commands/UserGuildSettingsCommands';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import {getMutedText, getNotificationSettingsLabel} from '@app/lib/overlay/contextMenu';
import {ChannelTypes, Permissions} from '@app/vendor/constants/ChannelConstants';
import {MessageNotifications} from '@app/vendor/constants/NotificationConstants';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

const logger = new Logger('CategoryMenuItems');
interface CategoryMenuItemProps {
	category: Channel;
	onClose: () => void;
}
export const MarkCategoryAsReadMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const channels = Channels.getGuildChannels(guildId);
	const channelsInCategory = useMemo(
		() => channels.filter((ch) => ch.parentId === category.id && ch.type !== ChannelTypes.GUILD_CATEGORY),
		[channels, category.id],
	);
	const hasUnread = channelsInCategory.some((ch) => ReadStates.hasUnread(ch.id));
	const handleMarkAsRead = useCallback(() => {
		for (const channel of channelsInCategory) {
			ReadStateCommands.ack(channel.id, true, true);
		}
		onClose();
	}, [channelsInCategory, onClose]);
	return (
		<MenuItem icon={<MarkAsReadIcon />} onClick={handleMarkAsRead} disabled={!hasUnread}>
			{t`Mark as Read`}
		</MenuItem>
	);
});
export const CollapseCategoryMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const isCollapsed = UserGuildSettings.isChannelCollapsed(guildId, category.id);
	const handleToggleCollapse = useCallback(() => {
		UserGuildSettingsCommands.toggleChannelCollapsed(guildId, category.id);
		onClose();
	}, [guildId, category.id, onClose]);
	return (
		<CheckboxItem checked={isCollapsed} onCheckedChange={handleToggleCollapse}>{t`Collapse Category`}</CheckboxItem>
	);
});
export const CollapseAllCategoriesMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const channels = Channels.getGuildChannels(guildId);
	const categoryIds = useMemo(
		() => channels.filter((ch) => ch.type === ChannelTypes.GUILD_CATEGORY).map((ch) => ch.id),
		[channels],
	);
	const allCategoriesCollapsed = useMemo(() => {
		if (categoryIds.length === 0) return false;
		return categoryIds.every((categoryId) => UserGuildSettings.isChannelCollapsed(guildId, categoryId));
	}, [guildId, categoryIds]);
	const handleToggleCollapseAll = useCallback(() => {
		UserGuildSettingsCommands.toggleAllCategoriesCollapsed(guildId, categoryIds);
		onClose();
	}, [guildId, categoryIds, onClose]);
	return (
		<CheckboxItem checked={allCategoriesCollapsed} onCheckedChange={handleToggleCollapseAll}>
			{t`Collapse All Categories`}
		</CheckboxItem>
	);
});
export const MuteCategoryMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t, i18n} = useLingui();
	const muteDurations = useMemo(() => getMuteDurationOptions(i18n), [i18n]);
	const guildId = category.guildId!;
	const categoryOverride = UserGuildSettings.getChannelOverride(guildId, category.id);
	const isMuted = categoryOverride?.muted ?? false;
	const muteConfig = categoryOverride?.mute_config;
	const mutedText = getMutedText(isMuted, muteConfig);
	const handleMute = useCallback(
		(duration: number | null) => {
			const nextMuteConfig = duration
				? {
						selected_time_window: duration,
						end_time: new Date(Date.now() + duration).toISOString(),
					}
				: null;
			UserGuildSettingsCommands.updateChannelOverride(guildId, category.id, {
				muted: true,
				mute_config: nextMuteConfig,
				collapsed: true,
			});
			onClose();
		},
		[guildId, category.id, onClose],
	);
	const handleUnmute = useCallback(() => {
		UserGuildSettingsCommands.updateChannelOverride(guildId, category.id, {
			muted: false,
			mute_config: null,
		});
		onClose();
	}, [guildId, category.id, onClose]);
	if (isMuted && mutedText) {
		return (
			<MenuItem icon={<MuteIcon />} onClick={handleUnmute} hint={mutedText}>
				{t`Unmute Category`}
			</MenuItem>
		);
	}
	return (
		<MenuItemSubmenu
			label={t`Mute Category`}
			onTriggerSelect={() => handleMute(null)}
			render={() => (
				<MenuGroup>
					{muteDurations.map((duration) => (
						<MenuItem key={duration.value ?? 'until'} onClick={() => handleMute(duration.value)}>
							{duration.label}
						</MenuItem>
					))}
				</MenuGroup>
			)}
		/>
	);
});
export const CategoryNotificationSettingsMenuItem: React.FC<CategoryMenuItemProps> = observer(({category}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const categoryNotifications = UserGuildSettings.getChannelOverride(guildId, category.id)?.message_notifications;
	const currentNotificationLevel = categoryNotifications ?? MessageNotifications.INHERIT;
	const guildNotificationLevel = UserGuildSettings.getGuildMessageNotifications(guildId);
	const effectiveCurrentNotificationLevel =
		currentNotificationLevel === MessageNotifications.INHERIT ? guildNotificationLevel : currentNotificationLevel;
	const currentStateText = getNotificationSettingsLabel(effectiveCurrentNotificationLevel);
	const defaultLabelParts = {
		main: t`Community Default`,
		sub: getNotificationSettingsLabel(guildNotificationLevel) ?? null,
	};
	const handleNotificationLevelChange = useCallback(
		(level: number) => {
			if (level === MessageNotifications.INHERIT) {
				UserGuildSettingsCommands.updateChannelOverride(guildId, category.id, {
					message_notifications: MessageNotifications.INHERIT,
				});
			} else {
				UserGuildSettingsCommands.updateMessageNotifications(guildId, level, category.id);
			}
		},
		[guildId, category.id],
	);
	return (
		<MenuItemSubmenu
			label={t`Notification Settings`}
			hint={currentStateText}
			render={() => (
				<MenuGroup>
					<MenuItemRadio
						selected={currentNotificationLevel === MessageNotifications.INHERIT}
						onSelect={() => handleNotificationLevelChange(MessageNotifications.INHERIT)}
					>
						<div className={itemStyles.flexColumn}>
							<span>{defaultLabelParts.main}</span>
							{defaultLabelParts.sub && <div className={menuItemStyles.subtext}>{defaultLabelParts.sub}</div>}
						</div>
					</MenuItemRadio>
					<MenuItemRadio
						selected={currentNotificationLevel === MessageNotifications.ALL_MESSAGES}
						onSelect={() => handleNotificationLevelChange(MessageNotifications.ALL_MESSAGES)}
					>
						{t`All Messages`}
					</MenuItemRadio>
					<MenuItemRadio
						selected={currentNotificationLevel === MessageNotifications.ONLY_MENTIONS}
						onSelect={() => handleNotificationLevelChange(MessageNotifications.ONLY_MENTIONS)}
					>
						{t`Only Mentions`}
					</MenuItemRadio>
					<MenuItemRadio
						selected={currentNotificationLevel === MessageNotifications.NO_MESSAGES}
						onSelect={() => handleNotificationLevelChange(MessageNotifications.NO_MESSAGES)}
					>
						{t`Nothing`}
					</MenuItemRadio>
				</MenuGroup>
			)}
		/>
	);
});
export const EditCategoryMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const canManageChannels = Permission.can(Permissions.MANAGE_CHANNELS, {
		channelId: category.id,
		guildId,
	});
	const handleEditCategory = useCallback(() => {
		ModalCommands.push(modal(() => <ChannelSettingsModal channelId={category.id} />));
		onClose();
	}, [category.id, onClose]);
	if (!canManageChannels) return null;
	return (
		<MenuItem icon={<SettingsIcon />} onClick={handleEditCategory}>
			{t`Edit Category`}
		</MenuItem>
	);
});
export const DeleteCategoryMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t} = useLingui();
	const guildId = category.guildId!;
	const canManageChannels = Permission.can(Permissions.MANAGE_CHANNELS, {
		channelId: category.id,
		guildId,
	});
	const channels = Channels.getGuildChannels(guildId);
	const channelsInCategory = useMemo(
		() => channels.filter((ch) => ch.parentId === category.id && ch.type !== ChannelTypes.GUILD_CATEGORY),
		[channels, category.id],
	);
	const handleDeleteCategory = useCallback(() => {
		onClose();
		const categoryName = category.name ?? '';
		const channelCount = channelsInCategory.length;
		const hasChannels = channelCount > 0;
		const description = hasChannels ? (
			<Trans>
				Are you sure you want to delete the category "{categoryName}"? All{' '}
				<Plural value={channelCount} one="# channel" other="# channels" /> inside will be moved to the top of the
				channel list.
			</Trans>
		) : (
			t`Are you sure you want to delete the category "${categoryName}"? This cannot be undone.`
		);
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Delete Category`}
					description={description}
					primaryText={t`Delete Category`}
					primaryVariant="danger-primary"
					onPrimary={async () => {
						try {
							await ChannelCommands.remove(category.id);
							ToastCommands.createToast({
								type: 'success',
								children: t`Category deleted`,
							});
						} catch (error) {
							logger.error('Failed to delete category:', error);
							ToastCommands.createToast({
								type: 'error',
								children: t`Failed to delete category`,
							});
						}
					}}
				/>
			)),
		);
	}, [category.id, category.name, channelsInCategory.length, onClose, t]);
	if (!canManageChannels) return null;
	return (
		<MenuItem icon={<DeleteIcon />} onClick={handleDeleteCategory} danger>
			{t`Delete Category`}
		</MenuItem>
	);
});
export const CopyCategoryIdMenuItem: React.FC<CategoryMenuItemProps> = observer(({category, onClose}) => {
	const {t, i18n} = useLingui();
	const handleCopyId = useCallback(() => {
		TextCopyCommands.copy(i18n, category.id);
		onClose();
	}, [category.id, onClose, i18n]);
	return (
		<MenuItem icon={<CopyIdIcon />} onClick={handleCopyId}>
			{t`Copy Category ID`}
		</MenuItem>
	);
});
